import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  buildCommandItems,
  filterCommandItems,
  type CommandItem,
} from "./command-math";
import {
  recentToCommandItems,
  searchDeskCommandResults,
} from "./desk-command-search";
import { loadRecentDocs } from "./recent-docs";

const SEARCH_DEBOUNCE_MS = 280;

/** ⌘K / Ctrl+K Desk jump + create + Recent/Results — plan/36 · plan/35 F */
export function CommandMenu(props: {
  companySlug: string;
  held: readonly string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { companySlug, held, open, onOpenChange } = props;
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [recentTick, setRecentTick] = useState(0);
  const [results, setResults] = useState<CommandItem[]>([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const catalog = useMemo(
    () => buildCommandItems(companySlug, held),
    [companySlug, held],
  );

  const recent = useMemo(() => {
    void recentTick;
    return recentToCommandItems(companySlug, loadRecentDocs(companySlug));
  }, [companySlug, recentTick]);

  const qTrim = query.trim();
  const staticFiltered = useMemo(
    () => filterCommandItems(catalog, qTrim),
    [catalog, qTrim],
  );

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActive(0);
    setResults([]);
    setSearching(false);
    setRecentTick((n) => n + 1);
    const t = window.setTimeout(() => inputRef.current?.focus(), 10);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query, results]);

  useEffect(() => {
    if (!open) return;
    if (qTrim.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    const ac = new AbortController();
    setSearching(true);
    const t = window.setTimeout(() => {
      void searchDeskCommandResults({
        companySlug,
        held,
        query: qTrim,
        signal: ac.signal,
      })
        .then((hits) => {
          if (!ac.signal.aborted) setResults(hits);
        })
        .catch(() => {
          if (!ac.signal.aborted) setResults([]);
        })
        .finally(() => {
          if (!ac.signal.aborted) setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      ac.abort();
      window.clearTimeout(t);
    };
  }, [open, qTrim, companySlug, held]);

  const go = staticFiltered.filter((i) => i.group === "Go");
  const create = staticFiltered.filter((i) => i.group === "Create");
  const showRecent = qTrim.length === 0 && recent.length > 0;
  const showResults = qTrim.length >= 2;

  const flat = [
    ...(showRecent ? recent : []),
    ...(showResults ? results : []),
    ...go,
    ...create,
  ];

  function run(item: CommandItem) {
    onOpenChange(false);
    navigate(item.path);
  }

  let offset = 0;
  const recentOffset = 0;
  if (showRecent) offset += recent.length;
  const resultsOffset = offset;
  if (showResults) offset += results.length;
  const goOffset = offset;
  offset += go.length;
  const createOffset = offset;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[min(100%-1.5rem,28rem)] gap-0 overflow-hidden p-0"
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((i) => Math.min(i + 1, Math.max(0, flat.length - 1)));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            const item = flat[active];
            if (item) run(item);
          }
        }}
      >
        <DialogTitle className="sr-only">Search</DialogTitle>
        <DialogDescription className="sr-only">
          Search documents, jump to a page, or create
        </DialogDescription>
        <div className="border-b border-[var(--color-border)] px-3 py-2.5">
          <Input
            ref={inputRef}
            className="border-0 bg-transparent shadow-none focus-visible:ring-0"
            placeholder="Search docs, go to… or create"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="max-h-[min(60vh,22rem)] overflow-y-auto p-2">
          {flat.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-[var(--color-muted)]">
              {searching
                ? "Searching…"
                : qTrim.length === 1
                  ? "Type at least 2 characters"
                  : "No matches"}
            </p>
          ) : (
            <>
              {showRecent ? (
                <CommandGroup
                  title="Recent"
                  items={recent}
                  activeId={flat[active]?.id}
                  offset={recentOffset}
                  onRun={run}
                  onHover={(id) => {
                    const i = flat.findIndex((x) => x.id === id);
                    if (i >= 0) setActive(i);
                  }}
                />
              ) : null}
              {showResults && results.length > 0 ? (
                <CommandGroup
                  title="Results"
                  items={results}
                  activeId={flat[active]?.id}
                  offset={resultsOffset}
                  onRun={run}
                  onHover={(id) => {
                    const i = flat.findIndex((x) => x.id === id);
                    if (i >= 0) setActive(i);
                  }}
                />
              ) : null}
              {showResults && searching && results.length === 0 ? (
                <p className="px-2.5 py-2 text-xs text-[var(--color-muted)]">
                  Searching…
                </p>
              ) : null}
              {go.length > 0 && (
                <CommandGroup
                  title="Go"
                  items={go}
                  activeId={flat[active]?.id}
                  offset={goOffset}
                  onRun={run}
                  onHover={(id) => {
                    const i = flat.findIndex((x) => x.id === id);
                    if (i >= 0) setActive(i);
                  }}
                />
              )}
              {create.length > 0 && (
                <CommandGroup
                  title="Create"
                  items={create}
                  activeId={flat[active]?.id}
                  offset={createOffset}
                  onRun={run}
                  onHover={(id) => {
                    const i = flat.findIndex((x) => x.id === id);
                    if (i >= 0) setActive(i);
                  }}
                />
              )}
            </>
          )}
        </div>
        <p className="border-t border-[var(--color-border)] px-3 py-2 text-[11px] text-[var(--color-muted)]">
          ↑↓ to move · Enter to open · Esc to close
        </p>
      </DialogContent>
    </Dialog>
  );
}

function CommandGroup(props: {
  title: string;
  items: CommandItem[];
  activeId?: string;
  offset: number;
  onRun: (item: CommandItem) => void;
  onHover: (id: string) => void;
}) {
  return (
    <div className="mb-2">
      <p className="m-0 px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-muted-fg)]">
        {props.title}
      </p>
      <ul className="m-0 list-none space-y-0.5 p-0">
        {props.items.map((item) => {
          const active = item.id === props.activeId;
          return (
            <li key={item.id}>
              <button
                type="button"
                className={[
                  "flex w-full items-center justify-between gap-3 rounded-xl px-2.5 py-2.5 text-left text-sm",
                  active
                    ? "bg-[var(--color-foreground)] text-[var(--color-card)]"
                    : "text-[var(--color-foreground)] hover:bg-[var(--color-background)]",
                ].join(" ")}
                onMouseEnter={() => props.onHover(item.id)}
                onClick={() => props.onRun(item)}
              >
                <span className="font-medium">{item.label}</span>
                {item.hint ? (
                  <span
                    className={
                      active
                        ? "text-[11px] opacity-70"
                        : "text-[11px] text-[var(--color-muted)]"
                    }
                  >
                    {item.hint}
                  </span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function useCommandMenuHotkey(opts: {
  enabled: boolean;
  onOpen: () => void;
}) {
  useEffect(() => {
    if (!opts.enabled) return;
    function onKey(e: KeyboardEvent) {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== "k") return;
      e.preventDefault();
      opts.onOpen();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [opts.enabled, opts.onOpen]);
}
